import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { editApiKey } from '../../services/ApiKeysService';
import { EditApiKeyRequest, ApiKeyResponse } from '../../models/ApiKeyResponse';

interface ApiKeyEditProps {
  apiKey: ApiKeyResponse;
  afterSave: () => void;
}

const ApiKeyEdit: React.FC<ApiKeyEditProps> = ({ apiKey, afterSave }) => {
  const { t } = useTranslation();
  const [apiError, setApiError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    description: apiKey.description || '',
    status: apiKey.status as string
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const statusOptions = [
    { label: t('active'), value: 'ACTIVE' },
    { label: t('inactive'), value: 'INACTIVE' },
  ];

  useEffect(() => {
    reset({
      description: apiKey.description || '',
      status: apiKey.status as string
    });
    setFormData({
      description: apiKey.description || '',
      status: apiKey.status as string
    });
  }, [apiKey, reset]);

  const onSubmit = async (formData: any) => {
    try {
      setApiError(null);
      const request: EditApiKeyRequest = { 
        description: formData.description || null,
        status: formData.status
      };
      const result = await editApiKey(apiKey.key, request);
      if (result.success) {
        afterSave();
      }
    } catch (error: any) {
      console.error('Failed to edit API key:', error);
      setApiError(error.response?.data?.error || t('editApiKeyError'));
    }
  };

  const clearApiError = () => {
    setApiError(null);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, description: e.target.value }));
  };

  const handleStatusChange = (e: any) => {
    setFormData(prev => ({ ...prev, status: e.value as string }));
  };

  return (
    <div className="">
      <form onSubmit={handleSubmit(onSubmit)} className="form-box__form">
        <div className="form-box__row">
          <div className="form-box__label">{t('description')}:</div>
          <InputText
            onInput={clearApiError}
            {...register('description')}
            value={formData.description}
            placeholder={t('enterDescription')}
            className={errors.description || apiError ? 'form-box__invalid' : ''}
            onChange={handleDescriptionChange}
          />
          {errors.description && <span>{errors.description.message as string}</span>}
        </div>
        
        <div className="form-box__row">
          <div className="form-box__label">{t('status')}:</div>
          <Dropdown
            {...register('status')}
            value={formData.status}
            options={statusOptions}
            placeholder={t('selectStatus')}
            className="dropdown"
            optionLabel="label"
            optionValue="value"
            onChange={handleStatusChange}
          />
        </div>
        
        {apiError && (
          <div className="p-error">
            {apiError}
          </div>
        )}

        <Button className="btn btn--sm" label={t('save')} type="submit" />
      </form>
    </div>
  );
};

export default ApiKeyEdit;
